// Attendance Insights, slice 1: trust, then cost.
//
// Coverage is on top and is never optional. A punctuality or overtime figure
// computed over a third of a roster is not slightly wrong, it is a different
// number — so this panel states how much of the expected data actually exists
// BEFORE any total is shown, and when it is short every total below wears an
// "at least". Nothing here is extrapolated to fill the gap.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, CheckCircle2, Clock, Users } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { DataState } from '@/features/shared/components/ui/DataState';
import { getApiErrorMessage } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import * as api from '@/services/attendanceService';
import type { AttendanceAnalytics } from '@/services/attendanceService';
import { listBranches } from '@/lib/hrApi';
import {
  coverageSentence, costQualifier, daysBehind, hoursOf, monthLabel, monthsInRange, neverSeenNames, periodRange,
  VERDICT_TONE,
  type PeriodKey,
} from './insights';

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_3_months', label: 'Last 3 months' },
];

const TONE_TEXT = {
  success: 'text-success', warning: 'text-warning',
  danger: 'text-danger', muted: 'text-muted-foreground',
} as const;

// Literal hex, as the other charts in this app do: Recharts writes `fill`
// straight onto the SVG and into the legend swatch, where neither a Tailwind
// class nor a CSS variable resolves.
const APPROVED_FILL = '#16A34A';
const PENDING_FILL = '#D97706';

/** One number with its unit and the sentence that explains it. Four of these
 *  side by side is the whole "what is it costing" panel. */
const Figure: React.FC<{
  value: number | string;
  unit?: string;
  label: string;
  tone?: 'plain' | 'warning';
}> = ({ value, unit, label, tone = 'plain' }) => (
  <div className="min-w-0">
    <div className="flex items-baseline gap-1.5">
      <span className={cn('text-3xl font-semibold tabular-nums',
        tone === 'warning' ? 'text-warning' : 'text-foreground')}>
        {value}
      </span>
      {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
    </div>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

export const AttendanceInsightsTab: React.FC = () => {
  const { t } = useTranslation('attendance');
  const [period, setPeriod] = useState<PeriodKey>('last_month');
  const [data, setData] = useState<AttendanceAnalytics | null>(null);
  const [branchNames, setBranchNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bump, setBump] = useState(0);

  const range = useMemo(() => periodRange(period), [period]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .getAnalytics(range.from, range.to)
      .then((r) => { if (alive) setData(r); })
      .catch((e) => {
        if (alive) {
          setData(null);
          setError(getApiErrorMessage(e, t('insights_failed', 'Couldn’t load attendance insights.') as string));
        }
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [range.from, range.to, bump, t]);

  useEffect(() => {
    let alive = true;
    listBranches()
      .then((bs) => {
        if (!alive) return;
        setBranchNames(Object.fromEntries((bs || []).map((b) => [String(b.id), b.name])));
      })
      .catch(() => { /* names are a nicety; ids still identify the row */ });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-36 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <DataState
        variant="error"
        message={error || (t('insights_failed', 'Couldn’t load attendance insights.') as string)}
        onRetry={() => setBump((n) => n + 1)}
        retryLabel={t('retry', 'Retry') as string}
      />
    );
  }

  const { trust, cost, time, branches } = data;
  const tone = VERDICT_TONE[trust.verdict];
  // Against the period end, not today — September is not "behind" in October.
  const behind = daysBehind(trust.covered_through, data.range.to);
  const qualifier = costQualifier(trust);
  // Zero-filled across the whole range — see monthsInRange.
  const byMonth = new Map(cost.months.map((m) => [m.month, m]));
  const chart = monthsInRange(data.range.from, data.range.to).map((key) => {
    const m = byMonth.get(key);
    return {
      month: monthLabel(key),
      approved: hoursOf(m?.approved_minutes || 0),
      pending: hoursOf(m?.pending_minutes || 0),
    };
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button
            type="button"
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={cn(
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              period === p.key
                ? 'bg-charcoal text-white'
                : 'border border-border bg-card text-muted-foreground hover:bg-muted',
            )}
          >
            {t(`insights_period_${p.key}`, p.label)}
          </button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {`${data.range.from} → ${data.range.to} · ${data.range.working_days} `}
          {t('insights_working_days', 'working days')}
        </span>
      </div>

      {/* ---- 1. Can I trust this? Always first. -------------------------- */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('insights_trust', 'Can I trust this?')}
              </p>
              <div className="mt-1 flex items-baseline gap-2">
                <span className={cn('text-4xl font-semibold tabular-nums', TONE_TEXT[tone])}>
                  {trust.roster_available ? `${trust.coverage_pct}%` : '—'}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t('insights_covered', 'of expected days have a record')}
                </span>
              </div>
              <p className="mt-1.5 max-w-prose text-sm text-charcoal">{coverageSentence(trust)}</p>
            </div>
            <div className="grid shrink-0 grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <span className="text-muted-foreground">{t('insights_people', 'People')}</span>
              <span className="tabular-nums text-foreground">{trust.employees}</span>
              <span className="text-muted-foreground">{t('insights_through', 'Covered through')}</span>
              <span className="tabular-nums text-foreground">
                {trust.covered_through || '—'}
                {behind != null && behind > 0 && (
                  <span className="ml-1.5 text-warning">
                    {t('insights_days_behind', '({{days}}d behind)', { days: behind })}
                  </span>
                )}
              </span>
              <span className="text-muted-foreground">{t('insights_open_exc', 'Open exceptions')}</span>
              <span className="tabular-nums text-foreground">
                {trust.open_exceptions}
                {trust.open_critical_exceptions > 0 && (
                  <span className="ml-1 text-danger">
                    ({trust.open_critical_exceptions} {t('insights_critical', 'critical')})
                  </span>
                )}
              </span>
            </div>
          </div>

          {trust.names_without_a_record.length > 0 && (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-warning/40 bg-warning-soft p-3 text-sm">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
              <span className="text-foreground">
                {t('insights_never_seen', {
                  defaultValue:
                    '{{count}} active employee(s) have no attendance record at all this period: {{names}}. Check their device mapping, or whether they should be on this roster.',
                  count: trust.names_without_a_record.length,
                  names: neverSeenNames(trust.names_without_a_record),
                })}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---- 2. What is it costing? -------------------------------------- */}
      <Card>
        <CardContent className="p-5">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('insights_cost', 'What is it costing?')}
          </p>

          {qualifier && (
            <div className="mt-2 flex items-start gap-2 text-sm text-warning" data-testid="cost-floor">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{qualifier}</span>
            </div>
          )}

          {/* Time lost applies to everyone. A salaried office with no overtime
              would otherwise read two zeros and never open this tab again. */}
          <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Figure
              value={time.unaccounted_days}
              unit={t('insights_days', 'days')}
              tone={time.unaccounted_days > 0 ? 'warning' : 'plain'}
              label={t('insights_unaccounted', 'Unaccounted for — no scan and no approved leave')}
            />
            <Figure
              value={time.late_days}
              unit={t('insights_days', 'days')}
              tone={time.late_days > 0 ? 'warning' : 'plain'}
              label={t('insights_late', 'Late starts — {{hours}} h in total', { hours: time.late_hours })}
            />
            <Figure
              value={time.early_days}
              unit={t('insights_days', 'days')}
              tone={time.early_days > 0 ? 'warning' : 'plain'}
              label={t('insights_early', 'Early finishes — {{hours}} h in total', { hours: time.early_hours })}
            />
            <Figure
              value={time.shortfall_hours}
              unit={t('insights_hours', 'hours')}
              tone={time.shortfall_minutes > 0 ? 'warning' : 'plain'}
              label={t('insights_shortfall', {
                defaultValue_one: 'Short of a standard day, across the 1 working day we hold',
                defaultValue_other: 'Short of a standard day, across the {{count}} working days we hold',
                count: time.workdays,
              })}
            />
          </div>

          {cost.has_overtime ? (
            <div className="mt-5 flex flex-wrap gap-8 border-t border-border pt-4">
              {/* Never promise payment for hours that never left this
                  service. Saying "payroll will pay this" directly above a red
                  alert that none of it was sent is the screen arguing with
                  itself, and the reader believes neither half. */}
              <Figure
                value={hoursOf(cost.approved_minutes)}
                unit={t('insights_hours', 'hours')}
                tone={cost.unsent_minutes > 0 ? 'warning' : 'plain'}
                label={
                  cost.unsent_minutes >= cost.approved_minutes && cost.approved_minutes > 0
                    ? t('insights_approved_ot_none_sent', 'Approved overtime — none of it sent to payroll yet')
                    : cost.unsent_minutes > 0
                      ? t('insights_approved_ot_partly_sent', 'Approved overtime — {{hours}} h of it not sent yet', {
                          hours: hoursOf(cost.unsent_minutes),
                        })
                      : t('insights_approved_ot', 'Approved overtime — payroll will pay this')
                }
              />
              <Figure
                value={hoursOf(cost.pending_minutes)}
                unit={t('insights_hours', 'hours')}
                tone={cost.pending_minutes > 0 ? 'warning' : 'plain'}
                label={t('insights_pending_ot', 'Pending — nobody has decided, so nobody is paid')}
              />
            </div>
          ) : (
            <p className="mt-5 border-t border-border pt-4 text-sm text-muted-foreground">
              {t('insights_no_ot', 'No overtime was recorded in this period.')}
            </p>
          )}

          {cost.unsent_minutes > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-danger/40 bg-danger-soft p-3 text-sm">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden />
              <span className="text-foreground">
                {t('insights_unsent', {
                  defaultValue:
                    'Of those, {{hours}} h have never been sent to payroll. The approval happened and the money did not move.',
                  hours: hoursOf(cost.unsent_minutes),
                })}
              </span>
            </div>
          )}

          {/* A single-month range has one bar, which is the number above it
              drawn larger. Suppressing it is deliberate — but a chart that is
              simply absent cannot be told from one that is broken, so say so
              rather than leaving a hole. */}
          {cost.has_overtime && chart.length === 1 && (
            <p className="mt-4 text-xs text-muted-foreground">
              {t('insights_chart_one_month', 'Pick “Last 3 months” to compare overtime month by month.')}
            </p>
          )}
          {cost.has_overtime && chart.length > 1 && (
            <div className="mt-4 h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={12} />
                  <YAxis tickLine={false} axisLine={false} fontSize={12} width={56} allowDecimals={false} />
                  {/* Recharts' default tooltip is a white card with pale text:
                      unreadable on the dark theme. These vars flip with it. */}
                  <Tooltip
                    contentStyle={{
                      background: 'var(--card)', color: 'var(--foreground)',
                      border: '1px solid var(--border)', borderRadius: 8, fontSize: 12,
                    }}
                    labelStyle={{ color: 'var(--foreground)', fontWeight: 600 }}
                    itemStyle={{ color: 'var(--foreground)' }}
                    cursor={{ fill: 'rgb(var(--color-charcoal-ch) / 0.06)' }}
                    formatter={(v: any, n: any) => [`${v} h`, n === 'approved'
                      ? t('insights_approved', 'Approved') : t('insights_pending', 'Pending')]}
                  />
                  {/* The formatter must return a STRING. Returning a JSX
                      element put "[object Object] legend icon" into the
                      accessibility tree — Recharts stringifies it for the
                      swatch's label. Colour comes from wrapperStyle. */}
                  <Legend
                    wrapperStyle={{ color: 'var(--muted-foreground)', fontSize: 12 }}
                    formatter={(v) => (v === 'approved'
                      ? String(t('insights_approved', 'Approved'))
                      : String(t('insights_pending', 'Pending')))}
                  />
                  <Bar dataKey="approved" fill={APPROVED_FILL} radius={[3, 3, 0, 0]} maxBarSize={56} />
                  <Bar dataKey="pending" fill={PENDING_FILL} radius={[3, 3, 0, 0]} maxBarSize={56} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---- By branch ---------------------------------------------------- */}
      {branches.length > 1 && (
        <Card>
          <CardContent className="p-5">
            <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">
              {t('insights_by_branch', 'By branch — worst coverage first')}
            </p>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">{t('branch', 'Branch')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('insights_people', 'People')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('insights_coverage', 'Coverage')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('insights_approved', 'Approved')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('insights_pending', 'Pending')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {branches.map((b) => (
                    <tr key={b.branch_id || 'none'}>
                      <td className="px-3 py-2 text-foreground">
                        {b.branch_id
                          ? branchNames[b.branch_id] || b.branch_id
                          : t('insights_no_branch', 'No branch set')}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{b.employees}</td>
                      <td className={cn('px-3 py-2 text-right tabular-nums', TONE_TEXT[VERDICT_TONE[b.verdict]])}>
                        {b.coverage_pct}%
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {hoursOf(b.approved_minutes)} h
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {hoursOf(b.pending_minutes)} h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {trust.verdict === 'good' && cost.pending_minutes === 0 && cost.unsent_minutes === 0 && (
        <div className="flex items-center gap-2 text-sm text-success">
          <CheckCircle2 className="h-4 w-4" aria-hidden />
          {t('insights_all_clear', 'Full coverage and nothing waiting on a decision.')}
        </div>
      )}
    </div>
  );
};
